<template>
  <div class="not-found" :class="{ 'is-mobile': isMobile }">
    <h1 class="title">{{ title }}</h1>

    <div v-if="slots.default" class="body">
      <p v-if="username">
        {{ usernameMessageParts[0] }}<code>@{{ username }}</code
        >{{ usernameMessageParts[1] }}
      </p>
      <slot />
    </div>

    <div v-else class="body">
      <p v-if="username">
        {{ usernameMessageParts[0] }}<code>@{{ username }}</code
        >{{ usernameMessageParts[1] }}
      </p>
      <p v-else v-html="message" />

      <GamButton
        v-if="!noExplore"
        @click="() => router.push(explorePath)"
        :variant="GamButtonVariant.SECONDARY"
        :size="GamButtonSize.SMALL"
        is-centered
      >
        {{ exploreLabel }}
      </GamButton>
    </div>

    <div v-if="!noClose" class="nav-close-btn" @click="handleClose">
      <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.X_LARGE" is-current />
    </div>
  </div>
</template>

<script setup lang="ts">
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import { localize } from '@/core/gambits';
import { isMobile } from '@/core/helpers/ui.helper';
import { ref, useSlots } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import GamButton from './components/GamButton.vue';
import GamIcon from './components/GamIcon.vue';
import { GamButtonSize, GamButtonVariant } from './composables/constants/components/gamButton.constants';
import { GamIconName, GamIconSize } from './composables/constants/components/gamIcon.constants';

export interface NotFoundProps {
  title?: string;
  message?: string;
  exploreLabel?: string;
  explorePath?: string;
  noExplore?: boolean;
  noClose?: boolean;
  username?: string;
}

withDefaults(defineProps<NotFoundProps>(), {
  title: () => localize('app.not_found.title'),
  message: () => localize('app.not_found.message'),
  exploreLabel: () => localize('app.not_found.explore'),
  explorePath: '/',
  username: '',
});

const slots = useSlots();
const router = useRouter();
const route = useRoute();
const isClosing = ref(false);
const usernameMessageParts = localize('app.not_found.username_message').split('%s');

const handleClose = () => {
  if (isClosing.value) return;
  isClosing.value = true;

  if (route.query.back && window.history.length > 1) {
    router.back();
  } else {
    router.replace({ name: RouterNameType.SPARKS });
  }
};
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.not-found {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  height: calc(100% - 115px);
}

.not-found {
  padding-bottom: calc(6 * var(--spacing-xxl));
}

.not-found.is-mobile {
  padding-bottom: calc(3 * var(--spacing-xxl));
}

.title {
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: var(--color-white-30);
}

.body {
  color: var(--color-white-70);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-l);
  padding: 0 var(--spacing-xl);
  text-align: center;
  max-width: 22.3em;
}

.body > * {
  line-height: 1.6;
  letter-spacing: 0.015em;
}

.title,
body {
  padding: 0 0 var(--spacing-l);
  margin: 0;
}

.nav-close-btn {
  position: fixed;
  bottom: var(--safe-bottom-offset);
  left: 50%;
  transform: translateX(-50%);
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1000;
  transition: all 200ms linear;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  @include press($transform: translateX(-50%));
}
</style>

<style lang="scss">
.not-found code {
  background-color: var(--color-white-70);
  color: var(--color-dark-700);
  padding-left: var(--spacing-xs);
  padding-right: var(--spacing-xs);
  border-radius: var(--radius-small);
}
</style>
